import { useMemo, useState } from 'react';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { ColumnsType } from 'antd/lib/table';
import { CurrentUserRole, HitStateType } from '@definitions/states';
import { TxKeyPath } from '@i18n';
import { sortDates, sortNumbers } from '@utils/sorters';
import { PresetTag, DataLengthTag } from '@components/display';
import { routes } from '@navigation/routes';
import { useNavigate, useParams } from 'react-router';
import { HitType, TaskType } from '@pages/types';
import { getStateKeyByValue } from '@utils';
import { css } from '@emotion/react';
import { GetTasksPayloadType } from '@api';
import {
  Content,
  Space,
  Dropdown,
  Tag,
  Text,
  Table,
  Row,
  colors,
  Button,
  Icon,
  Title,
  ITableProps,
  TMenuItemType as ItemType,
} from 'common-components';
import { LoadingOutlined } from '@ant-design/icons';

import { tasklineHitViews } from '../../shared';
import FiltersTasks from './FiltersTasks';

import { store } from '@/store';

export type TasklineRowType = (TaskType & { children?: HitType[] }) | HitType;

const isHit = (record: TasklineRowType): record is HitType => 'idHit' in record;

const taskField = <K extends keyof TaskType>(record: TasklineRowType, key: K) =>
  isHit(record) ? undefined : record[key];

type ProjectTasklinesPropsType = {
  tasks: TaskType[]
  tableProps?: Omit<ITableProps<TasklineRowType>, 'data' | 'columns'>
  onFiltersChange?: (filters: GetTasksPayloadType['filters']) => void
};

const TasksProject = ({ tasks, tableProps = {}, onFiltersChange }: ProjectTasklinesPropsType) => {
  const { translate } = useAppTranslation();
  const [isFiltersVisible, setFiltersVisible] = useState(false);
  const navigate = useNavigate();
  const params = useParams();

  const project = store.use.projects()?.find((project) => project?.idProject === params.projectId);
  const readEnabled =
    CurrentUserRole[project.currentUserRole] === 'user' ||
    CurrentUserRole[project.currentUserRole] === 'admin' ||
    CurrentUserRole[project.currentUserRole] === 'creator';
  const modifEnabled =
    CurrentUserRole[project.currentUserRole] === 'admin' ||
    CurrentUserRole[project.currentUserRole] === 'creator';

  const hits = store.use.hits();

  const getHitsAction = store.use.getHitsAction().call;
  const deleteTaskAction = store.use.deleteTaskAction().call;
  const increasePriorityTaskAction = store.use.increasePriorityTaskAction().call;
  const decreasePriorityTaskAction = store.use.decreasePriorityTaskAction().call;

  const isLoadingGetHitsAction = store.use.getHitsAction().status === 'LOADING';
  const isSucceededGetHitsAction = store.use.getHitsAction().status === 'SUCCEEDED';

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleExpand = (expanded: boolean, record: TaskType) => {
    if (expanded) {
      getHitsAction({ idTask: record.idTask });
    }
    setExpandedRowKeys(expanded ? [record.idTask] : []);
  };

  const getDropdownItems = (taskId: string, priority: number): ItemType[] => {
    const dropDownItems = [];

    if (readEnabled || modifEnabled) {
      dropDownItems.push({
        key: 1,
        label: <Text>{translate('projectsPage.dropdown.open')}</Text>,
        icon: <Icon icon={'ArrowCircleBrokenRight'} size={16} />,
        onClick: () => navigate(routes.projectTask.navigate(params.projectId, taskId)),
      });
    }
    if (modifEnabled) {
      dropDownItems.push({
        key: 2,
        label: (
          <Text>{translate('project.tasklineOverview.actionsItems.requestPrioritizing')}</Text>
        ),
        icon: <Icon icon={'ArrowUp'} />,
        onClick: () => increasePriorityTaskAction({ idTask: taskId }),
      });

      if (priority > 1) {
        dropDownItems.push({
          key: 3,
          label: (
            <Text>{translate('project.tasklineOverview.actionsItems.requestDeprioritizing')}</Text>
          ),
          icon: <Icon icon={'ArrowDown'} />,
          onClick: () => decreasePriorityTaskAction({ idTask: taskId }),
        });
      }

      dropDownItems.push({
        key: 4,
        label: <Text>{translate('project.tasklineOverview.actionsItems.deleteTask')}</Text>,
        icon: <Icon icon={'Trash01'} />,
        onClick: () => deleteTaskAction({ idTask: taskId }),
      });
    }
    return dropDownItems;
  };

  const getPriorityTag = (priority: TaskType['priority']) => {
    switch (true) {
      case priority >= 0 && priority < 20:
        return (
          <Tag style={{ background: colors.grayblue50, color: colors.grayblue500 }}>{priority}</Tag>
        );
      case priority >= 20 && priority < 40:
        return (
          <Tag
            style={{
              background: colors.grayblue200,
              color: colors.grayblue500,
            }}
          >
            {priority}
          </Tag>
        );
      case priority >= 40 && priority < 60:
        return (
          <Tag
            style={{
              background: colors.grayblue500,
              color: colors.grayblue100,
            }}
          >
            {priority}
          </Tag>
        );
      case priority >= 60 && priority < 80:
        return (
          <Tag style={{ background: colors.grayblue800, color: colors.grayblue25 }}>{priority}</Tag>
        );
      case priority >= 80:
        return (
          <Tag style={{ background: colors.grayblue900, color: colors.grayblue25 }}>{priority}</Tag>
        );
      default:
        return (
          <Tag style={{ background: colors.grayblue900, color: colors.grayblue25 }}>{priority}</Tag>
        );
    }
  };

  const goToHit = (hit: HitType) => {
    navigate(
      routes.projectTaskHit.navigate(
        params.projectId,
        hit.task.idTask,
        `${tasklineHitViews[0]}/${hit.idHit}`,
      ),
    );
  };

  const tableColumns: ColumnsType<TasklineRowType> = useMemo(() => {
    try {
      return [
        {
          title: translate('project.taskline.table.id'),
          key: 'idTask',
          width: 115,
          sorter: (a, b) => sortNumbers(taskField(a, 'idTask'), taskField(b, 'idTask')),
          render: (record: TasklineRowType) => {
            if (isHit(record)) {
              return (
                <Text size="sm" color={colors.gray900} style={{ display: 'block', fontWeight: 500, paddingLeft: 19 }}>
                  #{record.idHit}
                </Text>
              );
            }
            const isExpanded = expandedRowKeys.includes(record.idTask);
            return (
              <Row
                align={'middle'}
                wrap={false}
                style={{ gap: 4, cursor: 'pointer' }}
                onClick={() => handleExpand(!isExpanded, record)}
              >
                {isExpanded && isLoadingGetHitsAction ? (
                  <LoadingOutlined style={{ fontSize: 15 }} />
                ) : (
                  <Icon icon={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={15} css={$chevron} />
                )}
                <Text size="sm" color={colors.gray900} style={{ fontWeight: 500 }}>
                  #{record.idTask}
                </Text>
              </Row>
            );
          },
        },
        {
          title: translate('project.taskline.table.creationDate'),
          key: 'creationDate',
          width: 125,
          sorter: (a, b) => sortDates(taskField(a, 'creationDate'), taskField(b, 'creationDate')),
          render: (record: TasklineRowType) => (
            <Text size="xs">{isHit(record) ? '-' : record.creationDate}</Text>
          ),
        },
        {
          title: translate('project.taskline.table.origin'),
          key: 'origin',
          width: 125,
          sorter: (a, b) => sortNumbers(taskField(a, 'origin'), taskField(b, 'origin')),
          render: (record: TasklineRowType) => (
            <Text size="xs">
              {isHit(record)
                ? translate(`common.states.hitType.${record.hitType}`)
                : translate(`common.states.taskOrigin.${record.origin}` as TxKeyPath)}
            </Text>
          ),
        },
        {
          title: translate('project.taskline.table.priority'),
          key: 'priority',
          width: 91,
          sorter: (a, b) => sortNumbers(taskField(a, 'priority'), taskField(b, 'priority')),
          render: (record: TasklineRowType) =>
            isHit(record) ? <Text size="xs">-</Text> : getPriorityTag(record.priority),
        },
        {
          title: translate('project.taskline.table.deadline'),
          key: 'deleveringDate',
          width: 138,
          sorter: (a, b) =>
            sortDates(taskField(a, 'deleveringDate'), taskField(b, 'deleveringDate')),
          render: (record: TasklineRowType) => (
            <Text size="xs">{(!isHit(record) && record.deleveringDate) || '-'}</Text>
          ),
        },
        {
          title: translate('project.taskline.table.state'),
          key: 'taskState',
          width: 136,
          sorter: (a, b) => sortNumbers(taskField(a, 'taskState'), taskField(b, 'taskState')),
          render: (record: TasklineRowType) =>
            isHit(record) ? (
              <PresetTag withDot preset="hit-state" state={record.hitState}>
                {translate(`common.states.hitState.${record.hitState}`)}
              </PresetTag>
            ) : (
              <PresetTag withDot preset="task-state" state={record.taskState}>
                {translate(`common.states.taskState.${record.taskState}`)}
              </PresetTag>
            ),
        },
        /* HIDE
       * {
        title: translate("project.taskline.table.costs"),
        key: "totalCost",
        dataIndex: "totalCost",
        width: 86,
        sorter: (a, b) => sortNumbers(a.totalCost, b.totalCost),
        render: (totalCost) => (
          <Row align={"middle"}>
            <Space size={"middle"}>
              <Text size="xs">{totalCost} €</Text>
            </Space>
          </Row>
        ),
      },
      {
        title: translate("project.taskline.table.provider"),
        key: "provider",
        dataIndex: "provider",
        width: 141,
        render: () => (
          <Row align={"middle"}>
            <Space size={"middle"}>
            </Space>
          </Row>
        ),
      },*/
        {
          title: translate('project.taskline.table.note'),
          key: 'note',
          width: 114,
          render: (record: TasklineRowType) =>
            isHit(record) && record.hitState === getStateKeyByValue(HitStateType, 'completed') ? (
              <Button
                size={'inline'}
                onClick={(e) => {
                  e.stopPropagation();
                  goToHit(record);
                }}
              >
                {translate('project.taskline.table.rate')}
              </Button>
            ) : (
              <Text size="xs">-</Text>
            ),
        },
        {
          title: translate('project.taskline.table.time'),
          key: 'effectiveTime',
          width: 86,
          render: (record: TasklineRowType) => (
            <Text size="xs">{isHit(record) ? record.duration : record.effectiveTime}</Text>
          ),
        },
        {
          key: 'action',
          width: 63,
          render: (record: TasklineRowType) =>
            !isHit(record) && (
              <Row align={'middle'} justify={'center'}>
                <Dropdown
                  menu={{ items: getDropdownItems(record.idTask, record.priority) }}
                  placement={'bottomRight'}
                  styles={{ root: { width: 240 } }}
                >
                  <Icon icon={'DotsVertical'} size={20} />
                </Dropdown>
              </Row>
            ),
        },
      ];
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return [];
    }
  }, [expandedRowKeys, isLoadingGetHitsAction]);

  const rows: TasklineRowType[] = tasks.map((task) => ({
    ...task,
    children:
      expandedRowKeys.includes(task.idTask) && isSucceededGetHitsAction
        ? hits.map((hit) => ({ ...hit, task: { idTask: task.idTask } }))
        : [],
  }));

  return (
    <Content>
      <Space
        align="center"
        style={{
          width: '100%',
          marginBottom: 16,
          justifyContent: 'space-between',
        }}
      >
        <Space>
          <Title preset="singlePageTitle">{translate('project.taskline.title')}</Title>
          <DataLengthTag>{tasks?.length || 0}</DataLengthTag>
        </Space>
        <Button iconLeft="FilterLines" onClick={() => setFiltersVisible(true)}>
          {translate('common.filter')}
        </Button>
        <FiltersTasks
          visible={isFiltersVisible}
          onClose={() => setFiltersVisible(false)}
          onFiltersChange={onFiltersChange}
        />
      </Space>
      <Table<TasklineRowType>
        data={rows}
        columns={tableColumns}
        rowKey={(record) => (isHit(record) ? record.idHit : record.idTask)}
        rowClassName={(record) => (isHit(record) ? 'hit-row' : '')}
        onRow={(record) =>
          isHit(record) ? { onClick: () => goToHit(record), style: { cursor: 'pointer' } } : {}
        }
        css={css`
          .hit-row > td {
            background: ${colors.gray25};
          }
        `}
        expandable={{
          indentSize: 0,
          expandedRowKeys: expandedRowKeys,
          expandIcon: () => null,
        }}
        {...tableProps}
      />
    </Content>
  );
};

const $chevron = css`
  flex-shrink: 0;

  path {
    vector-effect: non-scaling-stroke;
  }
`;

export default TasksProject;
